/**
 * Tavern spell shop — center overlay listing the 16 buyable spells for the
 * friendly Tavern the player just stepped up to. E-key (or the building menu's
 * "Spell Shop" action) opens it; click a row's "Learn" to buy.
 *
 * Mirrors items/craft-menu.ts: factory shape `{ open, close, isOpen, refresh }`,
 * a `send` closure forwarding to room.send, and a `notify` toast callback.
 *
 * What the server owns (we only drive the UI):
 *   - `learnAbility` route → purchaseAbility(): validates alive / buyable /
 *     not-already-learned / Tavern-in-range / gold ≥ price / rider material,
 *     then debits + auto-binds to the first empty Q/W/E/R slot (≤4 learned).
 *     On failure it pushes `learnFailed` { reason } which main.ts toasts.
 *   - `bindSlot` route: rebind a learned spell onto a slot (for the 5th+ buy
 *     that sits unbound, or to reorganise). We render click-to-bind buttons
 *     rather than drag — Playwright-friendlier and the same server route.
 *
 * Affordability (gold + optional rider material) is pre-flighted here for a
 * "Need 10 more gold" toast before the click, but the server stays the source
 * of truth — a dimmed row is still clickable and the server silently/with-
 * reason rejects.
 *
 * Stable DOM: rows are built once at open() and refresh() only toggles classes
 * + slot-highlight, never re-renders innerHTML. (Re-rendering every frame
 * detaches the row a native click() resolved to — see building-menu.ts note.)
 */
import {
  ABILITIES,
  SPELL_SHOP_BLURBS,
  getMaterial,
  ITEMS,
  type AbilityDef,
  type ItemDef,
  type Cube,
} from "@tribez/shared";
import { abilityIconUrl } from "../hud/ability-icons";
import { itemIconUrl } from "../items/item-icons";

/** Open context — carries the Tavern building so the shop can host the owner's
 *  Salvage action in a footer (the building action-menu is skipped now that a
 *  left-click opens the shop directly). Optional: the E-key / building-menu
 *  paths open without it (no footer). */
export interface TavernCtx { buildingId?: string; isOwner?: boolean; }

export interface TavernShop {
  open(ctx?: TavernCtx): void;
  close(): void;
  isOpen(): boolean;
  /** Recompute owned/affordable/binding state. Call each frame while open. */
  refresh(cube: Cube): void;
}

type SendFn = (type: string, payload: unknown) => void;
type NotifyFn = (msg: string) => void;
type SlotKey = "Q" | "W" | "E" | "R";
const SLOTS: SlotKey[] = ["Q", "W", "E", "R"];

/** Human label for a rider material policy key (lowercase) — radiantGem →
 *  "Radiant Gem", shadowstone → "Shadowstone". */
function materialLabel(key: string): string {
  switch (key) {
    case "radiantGem": return "Radiant Gem";
    case "shadowstone": return "Shadowstone";
    default: return key;
  }
}

function isLearned(cube: Cube, abilityId: string): boolean {
  const learned = cube.spellbook.learned;
  for (let i = 0; i < learned.length; i++) if (learned[i] === abilityId) return true;
  return false;
}

function readSlot(cube: Cube, slot: SlotKey): string {
  switch (slot) {
    case "Q": return cube.spellbook.slotQ;
    case "W": return cube.spellbook.slotW;
    case "E": return cube.spellbook.slotE;
    case "R": return cube.spellbook.slotR;
  }
}

/** Compact "60 mana · 20.5s CD · Self · Spell" stat line from the def. */
function statLine(def: AbilityDef): string {
  const parts: string[] = [];
  if (def.cost.kind === "mana") parts.push(`${def.cost.amount} mana`);
  else if (def.cost.kind === "hp-sacrifice") parts.push(`${def.cost.percentMaxHp}% HP`);
  else parts.push("free");

  const cd = def.cooldownMs > 0 ? `${+(def.cooldownMs / 1000).toFixed(1)}s CD` : "no CD";
  parts.push(cd);

  switch (def.targeting.mode) {
    case "self": parts.push("self"); break;
    case "point": parts.push(`point r${def.targeting.range}`); break;
    case "unit": parts.push(`target r${def.targeting.range}`); break;
    case "toggle": parts.push("toggle"); break;
    case "channel": parts.push("channel"); break;
    case "point-charge": parts.push(`${def.targeting.chargesMax} charges`); break;
  }
  if (def.school !== "none") parts.push(def.school);
  return parts.join(" · ");
}

/** Price label: "40g" or "50g + 1 Radiant Gem". */
function priceLabel(def: AbilityDef): string {
  const t = def.tavern!;
  return t.resourceKey ? `${t.gold}g + 1 ${materialLabel(t.resourceKey)}` : `${t.gold}g`;
}

/** One-line effect summary for a consumable, derived from its cookingBuff
 *  (restore values) with a small fallback for the cleansing draught (no buff
 *  payload — its function is debuff removal). */
function consumableSummary(def: ItemDef): string {
  const b = def.cookingBuff;
  if (b) {
    const parts: string[] = [];
    if (b.restoreLife) parts.push(`+${b.restoreLife} Life`);
    if (b.restoreHunger) parts.push(`+${b.restoreHunger} Hunger`);
    if (b.restoreWarmth) parts.push(`+${b.restoreWarmth} Warmth`);
    if (b.applyBehavior) parts.push("buff");
    if (parts.length) return parts.join(" · ");
  }
  if (def.id === "CleansingDraught") return "Removes debuffs";
  return "Consumable";
}

interface RowRefs {
  el: HTMLDivElement;
  abilityId: string;
  gold: number;
  resourceKey?: string;
  buyBtn: HTMLButtonElement;
  slotBtns: Record<SlotKey, HTMLButtonElement>;
}

interface ConsumableRowRefs {
  el: HTMLDivElement;
  itemId: string;
  price: number;
}

interface PetRowRefs {
  el: HTMLDivElement;
  price: number;
}

export function createTavernShop(send: SendFn, notify?: NotifyFn): TavernShop {
  const root = document.createElement("div");
  root.id = "tavern-shop";
  root.style.display = "none";
  document.body.appendChild(root);

  let opened = false;
  let ctx: TavernCtx = {};
  let rows: RowRefs[] = [];
  let consumableRows: ConsumableRowRefs[] = [];
  let petRows: PetRowRefs[] = [];
  let lastCube: Cube | null = null;

  function renderList(): void {
    rows = [];
    consumableRows = [];
    petRows = [];
    root.innerHTML = `
      <div class="shop-menu__hdr">
        <span>Spell Shop</span>
        <span class="shop-menu__close">[Esc] close</span>
      </div>
      <div class="shop-menu__hint">Buying auto-binds to a free Q/W/E/R slot. Learned spells: click Q/W/E/R to rebind.</div>
      <div class="shop-menu__list"></div>
      ${ctx.isOwner && ctx.buildingId
        ? `<div class="shop-foot"><button class="shop-salvage" type="button">⚒ Salvage Tavern · 50% refund</button></div>`
        : ""}
    `;
    const list = root.querySelector<HTMLDivElement>(".shop-menu__list")!;
    root.querySelector<HTMLSpanElement>(".shop-menu__close")!
      .addEventListener("click", () => close());
    // Owner-only Salvage, relocated from the (now-skipped) building action menu.
    root.querySelector<HTMLButtonElement>(".shop-salvage")?.addEventListener("click", () => {
      if (ctx.buildingId) send("building-action", { id: ctx.buildingId, action: "salvage" });
      close();
    });

    // Buyable spells = those with a `tavern` entry. Sort by gold then name so
    // the cheap first-power-spike spells sit at the top.
    const buyable = Object.values(ABILITIES).filter((d) => d.tavern);
    buyable.sort((a, b) => (a.tavern!.gold - b.tavern!.gold) || a.name.localeCompare(b.name));

    for (const def of buyable) {
      const blurb = SPELL_SHOP_BLURBS[def.id] ?? "";
      const row = document.createElement("div");
      row.className = "spell-row";
      row.dataset.ability = def.id;
      row.innerHTML = `
        <div class="spell-row__icon" style="background-image:url('${abilityIconUrl(def.id)}')"></div>
        <div class="spell-row__main">
          <div class="spell-row__name">${def.name} <span class="spell-row__price">${priceLabel(def)}</span></div>
          <div class="spell-row__stats">${statLine(def)}</div>
          <div class="spell-row__blurb">${blurb}</div>
        </div>
        <div class="spell-row__action">
          <button class="spell-row__buy" type="button">Learn</button>
          <div class="spell-row__slots">
            ${SLOTS.map((s) => `<button class="spell-row__slot" data-slot="${s}" type="button">${s}</button>`).join("")}
          </div>
        </div>
      `;
      const buyBtn = row.querySelector<HTMLButtonElement>(".spell-row__buy")!;
      const slotBtns = {} as Record<SlotKey, HTMLButtonElement>;
      for (const s of SLOTS) {
        slotBtns[s] = row.querySelector<HTMLButtonElement>(`.spell-row__slot[data-slot="${s}"]`)!;
      }

      const abilityId = def.id;
      const gold = def.tavern!.gold;
      const resourceKey = def.tavern!.resourceKey;
      const name = def.name;

      buyBtn.addEventListener("click", (e) => {
        e.stopPropagation();
        // Pre-flight: explain a purchase that can't afford before the server
        // rejects. Server is still authoritative (incl. the Tavern-range check
        // we can't fully mirror — that lands as a learnFailed toast).
        if (lastCube) {
          if (isLearned(lastCube, abilityId)) return; // already owned — button hidden, but guard anyway
          const haveGold = getMaterial(lastCube, "gold");
          if (haveGold < gold) { notify?.(`Need ${gold - haveGold} more gold for ${name}`); return; }
          if (resourceKey && getMaterial(lastCube, resourceKey) < 1) {
            notify?.(`Need 1 ${materialLabel(resourceKey)} for ${name}`); return;
          }
        }
        send("learnAbility", { abilityId });
        notify?.(`Learning: ${name}`);
      });

      for (const s of SLOTS) {
        slotBtns[s].addEventListener("click", (e) => {
          e.stopPropagation();
          if (lastCube && !isLearned(lastCube, abilityId)) return; // only learned spells bind
          send("bindSlot", { slot: s, abilityId });
          notify?.(`Bound ${name} to ${s}`);
        });
      }

      list.appendChild(row);
      rows.push({ el: row, abilityId, gold, resourceKey, buyBtn, slotBtns });
    }

    // ── Consumables section ──────────────────────────────────────────────
    // Food & drink sold for gold (any item with a `tavernPrice`). Re-buyable
    // stackables — no owned-state, just a Buy button (dimmed when gold short).
    const consumables = Object.values(ITEMS)
      .filter((d) => d.tavernPrice != null)
      .sort((a, b) => (a.tavernPrice! - b.tavernPrice!) || a.displayName.localeCompare(b.displayName));

    if (consumables.length > 0) {
      const divider = document.createElement("div");
      divider.className = "shop-menu__section";
      divider.textContent = "Consumables — food & drink";
      list.appendChild(divider);

      for (const def of consumables) {
        const price = def.tavernPrice!;
        const itemId = def.id;
        const name = def.displayName;
        const row = document.createElement("div");
        row.className = "spell-row";
        row.dataset.item = itemId;
        row.innerHTML = `
          <div class="spell-row__icon" style="background-image:url('${itemIconUrl(itemId)}')"></div>
          <div class="spell-row__main">
            <div class="spell-row__name">${name} <span class="spell-row__price">${price}g</span></div>
            <div class="spell-row__blurb">${consumableSummary(def)}</div>
          </div>
          <div class="spell-row__action">
            <button class="spell-row__buy" type="button">Buy</button>
          </div>
        `;
        const buyBtn = row.querySelector<HTMLButtonElement>(".spell-row__buy")!;
        buyBtn.addEventListener("click", (e) => {
          e.stopPropagation();
          if (lastCube) {
            const haveGold = getMaterial(lastCube, "gold");
            if (haveGold < price) { notify?.(`Need ${price - haveGold} more gold for ${name}`); return; }
          }
          send("buyTavernItem", { itemId });
          notify?.(`Bought: ${name}`);
        });
        list.appendChild(row);
        consumableRows.push({ el: row, itemId, price });
      }
    }

    // ── Pets section ──────────────────────────────────────────────────────
    const NIGHTWISP_COST = 45;
    const petDivider = document.createElement("div");
    petDivider.className = "shop-menu__section";
    petDivider.textContent = "Pets";
    list.appendChild(petDivider);

    const petRow = document.createElement("div");
    petRow.className = "spell-row";
    petRow.dataset.pet = "Nightwisp";
    petRow.innerHTML = `
      <div class="spell-row__main">
        <div class="spell-row__name">Nightwisp <span class="spell-row__price">${NIGHTWISP_COST}g</span></div>
        <div class="spell-row__blurb">Ranged magic companion — fires a beam from 4.5u. Joins your pets (respects pet cap).</div>
      </div>
      <div class="spell-row__action">
        <button class="spell-row__buy" type="button">Buy</button>
      </div>`;
    const petBuyBtn = petRow.querySelector<HTMLButtonElement>(".spell-row__buy")!;
    petBuyBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      if (lastCube) {
        const haveGold = getMaterial(lastCube, "gold");
        if (haveGold < NIGHTWISP_COST) { notify?.(`Need ${NIGHTWISP_COST - haveGold} more gold for Nightwisp`); return; }
      }
      send("purchase-nightwisp", {});
      notify?.("Buying: Nightwisp");
    });
    list.appendChild(petRow);
    petRows.push({ el: petRow, price: NIGHTWISP_COST });
  }

  function open(openCtx: TavernCtx = {}): void {
    opened = true;
    ctx = openCtx;
    root.style.display = "flex";
    renderList();
  }

  function close(): void {
    opened = false;
    ctx = {};
    root.style.display = "none";
    root.innerHTML = "";
    rows = [];
    consumableRows = [];
    petRows = [];
  }

  function isOpen(): boolean { return opened; }

  function refresh(cube: Cube): void {
    if (!opened) return;
    lastCube = cube;
    const haveGold = getMaterial(cube, "gold");
    for (const r of rows) {
      const owned = isLearned(cube, r.abilityId);
      r.el.classList.toggle("spell-row--owned", owned);

      // Affordability dims the buy button only (owned rows hide it via CSS).
      let affords = haveGold >= r.gold;
      if (affords && r.resourceKey) affords = getMaterial(cube, r.resourceKey) >= 1;
      r.el.classList.toggle("spell-row--unaffordable", !owned && !affords);

      // Highlight the slot this spell is currently bound to (if any).
      for (const s of SLOTS) {
        r.slotBtns[s].classList.toggle("spell-row__slot--bound", owned && readSlot(cube, s) === r.abilityId);
      }
    }
    for (const c of consumableRows) {
      c.el.classList.toggle("spell-row--unaffordable", haveGold < c.price);
    }
    for (const p of petRows) {
      p.el.classList.toggle("spell-row--unaffordable", haveGold < p.price);
    }
  }

  return { open, close, isOpen, refresh };
}

/**
 * Nearest friendly, ready Tavern within `radius` of the cube. Mirrors the
 * server's findFriendlyBuildingNear("Tavern", INTERACT_RADIUS) check so the
 * E-key only opens the shop when the server would actually accept a buy.
 * INTERACT_RADIUS = 4 (items/craft.ts).
 */
export const TAVERN_INTERACT_RADIUS = 4;

export function findNearbyTavern(
  cube: Cube,
  buildings: Iterable<{ buildingType: string; teamId: number; state: string; x: number; z: number }>,
  radius: number = TAVERN_INTERACT_RADIUS,
): boolean {
  for (const b of buildings) {
    if (b.teamId !== cube.teamId) continue;
    if (b.state !== "ready") continue;
    if (b.buildingType !== "Tavern") continue;
    if (Math.hypot(b.x - cube.x, b.z - cube.z) <= radius) return true;
  }
  return false;
}
